import { createRouter, createWebHistory } from "vue-router";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import { checkAuth } from "@/utils/auth";
import { forceScrollToTop } from "@/utils/scroll";

const MainLayout = () =>
  import(/* webpackChunkName: "layout-main" */ "@/layouts/MainLayout.vue");
const MobileLayout = () =>
  import(/* webpackChunkName: "layout-mobile" */ "@/layouts/MobileLayout.vue");

// 配置 NProgress
NProgress.configure({
  showSpinner: false,
  minimum: 0.3,
  trickleSpeed: 200,
  easing: "ease",
  speed: 500,
});

// 检测设备类型
const isMobileDevice = () => {
  return window.innerWidth <= 768;
};

const routes = [
  {
    path: "/s/:shortCode",
    name: "ShortLink",
    component: () => import("@/views/ShortLink.vue"),
  },

  {
    path: "/forbidden-country",
    name: "ForbiddenCountryPc",
    component: () => import("@/views/ForbiddenCountry.vue"),
    meta: { layout: "none" },
  },
  {
    path: "/m/forbidden-country",
    name: "ForbiddenCountryMobile",
    component: () => import("@/views/ForbiddenCountry.vue"),
    meta: { layout: "none" },
  },

  // 桌面端路由
  {
    path: "/",
    component: MainLayout,
    redirect: (to: any) => ({
      path: "/home",
      query: to.query,
      hash: to.hash,
    }),
    children: [
      {
        path: "home",
        name: "HomeDesktop",
        component: () => import("@/views/Home/index.vue"),
      },
      {
        path: "home/:code",
        name: "HomeWithCodeDesktop",
        component: () => import("@/views/Home/index.vue"),
      },
      {
        path: "game-list",
        name: "GameListDesktop",
        component: () => import("@/views/Game/index.vue"),
      },
      {
        path: "game/:id",
        name: "GameDetailDesktop",
        component: () => import("@/views/Game/GameDetail.vue"),
        props: true,
        meta: { requiresAuth: true },
      },
      // 共用页面
      {
        path: "deposit",
        name: "Deposit",
        component: () => import("@/views/Deposit/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "withdraw",
        name: "Withdraw",
        component: () => import("@/views/Withdraw/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "withdraw/record",
        name: "WithdrawRecordDesktop",
        component: () => import("@/views/Withdraw/Record/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "account",
        name: "Account",
        component: () => import("@/views/Account/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "records",
        name: "Records",
        component: () => import("@/views/Records/records-new.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "connection",
        name: "Connection",
        component: () => import("@/views/Connection/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "aposta",
        name: "Aposta",
        component: () => import("@/views/Aposta/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "premiacao",
        name: "Premiacao",
        component: () => import("@/views/Premiacao/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "vip",
        name: "VIP",
        component: () => import("@/views/VIP/vip-new.vue"),
        meta: {
          layout: "desktop",
          requiresAuth: true,
        },
      },
      {
        path: "bonus",
        name: "MyBonus",
        component: () => import("@/views/MyBonus/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "messages",
        name: "Messages",
        component: () => import("@/views/Messages/index.vue"),
        meta: {
          layout: "desktop",
        },
      },
      {
        path: "invite",
        name: "Invite",
        component: () => import("@/views/Invite/invite-new.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "inviteRewardRules",
        name: "InviteRewardRules",
        component: () => import("@/views/Invite/invite-reward-rules.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "commission",
        name: "Commission",
        component: () => import("@/views/Commission/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "user/profile",
        name: "UserProfile",
        component: () => import("@/views/UserProfile/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "privacy-policy",
        name: "PrivacyPolicy",
        component: () => import("@/views/PrivacyPolicy/index.vue"),
        meta: {
          layout: "desktop",
        },
      },
      {
        path: "terms-of-service",
        name: "TermsOfService",
        component: () => import("@/views/TermsOfService/index.vue"),
        meta: {
          layout: "desktop",
        },
      },
      {
        path: "responsible-gaming",
        name: "ResponsibleGaming",
        component: () => import("@/views/ResponsibleGaming/index.vue"),
        meta: {
          layout: "desktop",
        },
      },
      {
        path: "cash-back-daily",
        name: "CashBackDailyDesktop",
        component: () => import("@/views/CashBack/daily.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "cash-back-weekly",
        name: "CashBackWeeklyDesktop",
        component: () => import("@/views/CashBack/weekly.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "bank-cards",
        name: "BankCardListDesktop",
        component: () => import("@/views/Withdraw/bindInfo.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "phone-binding",
        name: "PhoneBinding",
        component: () => import("@/views/PhoneBinding/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "Atividades-Pinduoduo",
        name: "PinduoduoActivityDesktop",
        component: () => import("@/views/Activity/PinduoduoNew.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "Pinduoduo-RewardRules",
        name: "PinduoduoRewardRulesDesktop",
        component: () => import("@/views/Activity/rule-detail.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "Opinion",
        name: "OpinionDesktop",
        component: () => import("@/views/Opinion/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
      {
        path: "HelpCenter",
        name: "HelpCenterDesktop",
        component: () => import("@/views/HelpCenter/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "desktop",
        },
      },
    ],
  },
  // 移动端路由
  {
    path: "/m",
    component: MobileLayout,
    redirect: (to: any) => ({
      path: "/m/home",
      query: to.query,
      hash: to.hash,
    }),
    children: [
      {
        path: "home",
        name: "HomeMobile",
        component: () => import("@/views/Mobile/Home/index.vue"),
      },
      {
        path: "home/:code",
        name: "HomeWithCodeMobile",
        component: () => import("@/views/Mobile/Home/index.vue"),
      },
      {
        path: "game-list",
        name: "GameListMobile",
        component: () => import("@/views/Mobile/Game/index.vue"),
      },
      {
        path: "game-manufacturer",
        name: "GameManufacturerMobile",
        component: () => import("@/views/Mobile/Game/GameList.vue"),
      },
      {
        path: "manufacturer",
        name: "ManufacturerMobile",
        component: () => import("@/views/Mobile/Game/GameManufacturer.vue"),
      },
      {
        path: "game/:id",
        name: "GameDetailMobile",
        component: () => import("@/views/Mobile/Game/GameDetail.vue"),
        props: true,
        meta: { requiresAuth: true },
      },
      {
        path: "user-info",
        name: "UserInfoMobile",
        component: () => import("@/views/Mobile/UserInfo.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "game-search",
        name: "GameSearchMobile",
        component: () => import("@/views/Mobile/Game/search.vue"),
      },
      {
        path: "customer-service",
        name: "CustomerServiceMobile",
        component: () => import("@/views/Mobile/Message/CustomerService.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "invite/78tt-invite",
        name: "Invite78TT",
        component: () => import("@/views/Invite/78tt-invite.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "invite/78tt-invite/details",
        name: "Invite78TTDetails",
        component: () => import("@/views/Invite/78tt-invite-details.vue"),
        meta: { requiresAuth: true },
      },
      // 共用页面
      {
        path: "deposit",
        name: "DepositMobile",
        component: () => import("@/views/Deposit/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "deposit/record",
        name: "DepositRecordMobile",
        component: () => import("@/views/Deposit/Record/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "withdraw",
        name: "WithdrawMobile",
        component: () => import("@/views/Withdraw/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "withdraw/record",
        name: "WithdrawRecordMobile",
        component: () => import("@/views/Withdraw/Record/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "account",
        name: "AccountMobile",
        component: () => import("@/views/Account/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "records",
        name: "RecordsMobile",
        component: () => import("@/views/Records/records-new.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "connection",
        name: "ConnectionMobile",
        component: () => import("@/views/Connection/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "aposta",
        name: "ApostaMobile",
        component: () => import("@/views/Aposta/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "premiacao",
        name: "PremiacaoMobile",
        component: () => import("@/views/Premiacao/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "bonus",
        name: "MyBonusMobile",
        component: () => import("@/views/MyBonus/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "security-center",
        name: "SecurityCenterMobile",
        component: () => import("@/views/Mobile/SecurityCenter/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "security-center/verify-password",
        name: "VerifyPasswordMobile",
        component: () => import("@/views/Mobile/SecurityCenter/VerifyPassword.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "message-detail",
        name: "MobileMessageDetail",
        component: () => import("@/views/Messages/MobileMessageDetail.vue"),
        meta: {
          layout: "mobile",
        },
      },
      {
        path: "messages",
        name: "MessagesMobile",
        component: () => import("@/views/Messages/index.vue"),
        meta: {
          layout: "mobile",
        },
      },
      {
        path: "invite",
        name: "InviteMobile",
        component: () => import("@/views/Invite/invite-new.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "inviteRewardRules",
        name: "InviteRewardRulesMobile",
        component: () => import("@/views/Invite/invite-reward-rules.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "commission",
        name: "CommissionMobile",
        component: () => import("@/views/Commission/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "user/profile",
        name: "UserProfileMobile",
        component: () => import("@/views/UserProfile/mobile-info.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "user/profile/edit",
        name: "UserProfileEditMobile",
        component: () => import("@/views/UserProfile/edit-perfil.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "profile/reports",
        name: "ProfileReportsMobile",
        component: () => import("@/views/Mobile/Profile/Reports/index.vue"),
        meta: {
          layout: "mobile",
          requiresAuth: true,
        },
      },
      {
        path: "privacy-policy",
        name: "PrivacyPolicyMobile",
        component: () => import("@/views/PrivacyPolicy/index.vue"),
        meta: {
          layout: "mobile",
        },
      },
      {
        path: "terms-of-service",
        name: "TermsOfServiceMobile",
        component: () => import("@/views/TermsOfService/index.vue"),
        meta: {
          layout: "mobile",
        },
      },
      {
        path: "responsible-gaming",
        name: "ResponsibleGamingMobile",
        component: () => import("@/views/ResponsibleGaming/index.vue"),
        meta: {
          layout: "mobile",
        },
      },
      {
        path: "bank-cards",
        name: "BankCardListMobile",
        component: () => import("@/views/BankCardlist/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "cash-back-daily",
        name: "CashBackDailyMobile",
        component: () => import("@/views/CashBack/daily.vue"),
        meta: {
          // requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "cash-back-weekly",
        name: "CashBackWeeklyMobile",
        component: () => import("@/views/CashBack/weekly.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "vip-details",
        name: "VipDetailsMobile",
        component: () => import("@/views/Mobile/VipDetails/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu",
        name: "ActivityMenuMobile",
        component: () => import("@/views/Mobile/ActivityMenu/index.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "promotion",
        name: "PromotionCenterMobile",
        component: () => import("@/views/Mobile/Promotion/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/vip-reward",
        name: "VipRewardMobile",
        component: () => import("@/views/Mobile/ActivityMenu/vip-reward.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/vip-reward-receber",
        name: "VipRewardReceberMobile",
        component: () => import("@/views/Mobile/ActivityMenu/vip-reward-receber.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/checkin-apostas",
        name: "CheckinApostasMobile",
        component: () => import("@/views/Mobile/ActivityMenu/checkin-apostas.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/aposta-radio",
        name: "ApostaRadioMobile",
        component: () => import("@/views/Mobile/ActivityMenu/aposta-radio.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/mina-misteriosa",
        name: "MinaMisteriosaMobile",
        component: () => import("@/views/Mobile/ActivityMenu/mina-misteriosa.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/pdd-activity",
        name: "PinduoduoActivityMobile",
        component: () => import("@/views/Mobile/ActivityMenu/pdd-activity.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/promotional-revenue",
        name: "PromotionalRevenueMobile",
        component: () => import("@/views/Mobile/ActivityMenu/promotional-revenue.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/mystery-bonus",
        name: "MysteryBonusMobile",
        component: () => import("@/views/Mobile/ActivityMenu/mystery-bonus.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "activity-menu/recharge-reward",
        name: "RechargeRewardMobile",
        component: () => import("@/views/Mobile/ActivityMenu/recharge-reward.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "s/:shortCode",
        name: "ShortLinkMobile",
        component: () => import("@/views/ShortLink.vue"),
        meta: {
          layout: "mobile",
        },
      },
      {
        path: "phone-binding",
        name: "PhoneBindingMobile",
        component: () => import("@/views/PhoneBinding/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      // {
      //   path: "Atividades-Pinduoduo",
      //   name: "PinduoduoActivityMobile",
      //   component: () => import("@/views/Activity/PinduoduoNew.vue"),
      //   meta: {
      //     requiresAuth: true,
      //     layout: "mobile",
      //   },
      // },
      {
        path: "Pinduoduo-RewardRules",
        name: "PinduoduoRewardRulesMobile",
        component: () => import("@/views/Activity/rule-detail.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "Opinion",
        name: "OpinionMobile",
        component: () => import("@/views/Opinion/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "HelpCenter",
        name: "HelpCenterMobile",
        component: () => import("@/views/HelpCenter/index.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "GameClassify",
        name: "GameClassifyMobile",
        component: () => import("@/views/Mobile/Game/GameClassify.vue"),
        meta: {
          requiresAuth: false,
          layout: "mobile",
        },
      },
      {
        path: "GameHis",
        name: "GameHisMobile",
        component: () => import("@/views/Mobile/History/GameHis.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "OperationHis",
        name: "OperationHisMobile",
        component: () => import("@/views/Mobile/History/OperationHis.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "TransactionHis",
        name: "TransactionHisMobile",
        component: () => import("@/views/Mobile/History/TransactionHis.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "SwitchAccount",
        name: "SwitchAccountMobile",
        component: () => import("@/views/Mobile/SwitchAccount/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
      {
        path: "Setting",
        name: "SettingMobile",
        component: () => import("@/views/Setting/index.vue"),
        meta: {
          requiresAuth: true,
          layout: "mobile",
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // 如果有保存的位置（浏览器前进/后退），则恢复
    if (savedPosition) {
      return savedPosition;
    }

    // 如果路由有 hash，滚动到对应元素
    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    }

    // 如果路由有 meta.scrollToTop 为 false，保持当前位置
    if (to.meta.scrollToTop === false) {
      return false;
    }

    // 默认滚动到顶部
    return {
      top: 0,
      behavior: "smooth", // 平滑滚动
    };
  },
});

// 修改设备切换处理函数
const handleDeviceSwitch = (to: any) => {
  console.log("handleDeviceSwitch", to);
  const path = to.path;
  const isMobilePath = path.startsWith("/m/") || path === "/m";

  // 特殊路由不进行设备切换
  if (path.startsWith("/s/") || path === "/activity/Atividades-Pinduoduo") {
    return true;
  }

  // 构建完整的重定向对象，包含查询参数和哈希值
  const buildRedirectRoute = (newPath: string) => {
    const redirectRoute: any = {
      path: newPath,
    };

    // 保留查询参数
    if (to.query && Object.keys(to.query).length > 0) {
      redirectRoute.query = to.query;
    }

    // 保留哈希值
    if (to.hash) {
      redirectRoute.hash = to.hash;
    }

    return redirectRoute;
  };

  // 如果访问的是桌面端路由（非 /m 开头），强制重定向到移动端
  if (!isMobilePath) {
    // 如果是根路径，重定向到 /m
    if (path === "/") {
      return buildRedirectRoute("/m");
    }
    // 其他路径直接加上 /m 前缀
    return buildRedirectRoute(`/m${path}`);
  }

  return true;
};

// 全局前置守卫
router.beforeEach((to, from, next) => {
  console.log("beforeEach", to, from);
  // 先检查设备类型并处理重定向
  const redirectPath = handleDeviceSwitch(to);
  console.log("redirectPath", redirectPath);
  if (redirectPath !== true) {
    next(redirectPath);
    return;
  }

  // 然后检查权限
  const result = checkAuth(to, from);
  if (result === true) {
    NProgress.start();
    next();
  } else {
    next(result);
  }
});

router.afterEach((to, from) => {
  NProgress.done();

  // 强制滚动到顶部（除了有hash的路由）
  if (!to.hash) {
    forceScrollToTop();
  }
});

// 监听窗口大小变化
window.addEventListener("resize", () => {
  const currentPath = router.currentRoute.value.path;
  const redirectPath = handleDeviceSwitch({ path: currentPath });
  if (redirectPath !== true) {
    router.replace(redirectPath);
  }
});

export default router;
